import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';

import { BusinessApiService } from '../services/business-api.service';
import { BusinessResponse } from '../models/business.model';
import { BusinessDetailModalComponent } from '../business-detail-modal/business-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

/** Mismo texto que devuelve el backend en el 409, para que la UI y la API digan lo mismo. */
const PROVIDER_IMMUTABLE_MESSAGE =
  'La empresa proveedora no se puede desactivar ni eliminar: es la única que garantiza el acceso del Super Admin al sistema.';

@Component({
  selector: 'app-business-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ModalComponent,
    BusinessDetailModalComponent,
    HasPermissionDirective,
    DisableWithoutPermissionDirective
  ],
  templateUrl: './business-list.component.html'
})
export class BusinessListComponent implements OnInit {
  allBusinesses: BusinessResponse[] = [];
  businesses: BusinessResponse[] = [];

  loading = false;
  error = '';

  showFilters = false;
  filtroNombre = '';
  filtroActivo: boolean | undefined;

  sortBy: 'name' | 'identification' = 'name';
  sortDir: 'asc' | 'desc' = 'asc';

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'business-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  page = 1;
  totalFiltered = 0;
  totalPages = 1;

  get globalPage(): number { return this.page - 1; }
  get globalTotalPages(): number { return this.totalPages; }
  get globalTotalElements(): number { return this.totalFiltered; }
  get hasPrevPage(): boolean { return this.page > 1; }
  get hasNextPage(): boolean { return this.page < this.totalPages; }
  get pageDisplay(): number { return this.page; }
  get pagesArray(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }

  isDetailOpen = false;
  selectedBusiness: BusinessResponse | null = null;
  detailLoading = false;

  // Diálogo de eliminación con contraseña (INC 16). Solo aplica a negocios inactivos.
  showDeleteModal = false;
  deleteTarget: BusinessResponse | null = null;
  deletePassword = '';
  deleteError = '';
  deleting = false;
  showDeletePassword = false;

  /** Id del negocio cuyo estado se está cambiando (deshabilita el switch mientras dura). */
  togglingId: number | null = null;

  private readonly SEARCH_BATCH_SIZE = 2000;

  constructor(
    private businessApi: BusinessApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarBusinesses();
  }

  cargarBusinesses(): void {
    this.loading = true;
    this.error = '';

    const usingFallback = this.hasActiveFiltersOrSorting();

    if (usingFallback) {
      this.businessApi.getAll(0, this.SEARCH_BATCH_SIZE).subscribe({
        next: (res) => {
          const raw = res?.content ?? [];
          this.allBusinesses = raw.map(x => this.normalizeBusiness(x));
          this.applyClientFilters(true);
          this.loading = false;
        },
        error: (err) => {
          this.error = this.extractErrorMessage(err, 'Error al cargar businesses');
          this.loading = false;
        }
      });
      return;
    }

    const backendPage = Math.max(0, this.page - 1);

    this.businessApi.getAll(backendPage, this.pageSize).subscribe({
      next: (res) => {
        this.allBusinesses = [];
        const raw = res?.content ?? [];
        this.businesses = raw.map(x => this.normalizeBusiness(x));

        this.totalFiltered = res?.totalElements ?? this.businesses.length;
        this.totalPages = Math.max(1, res?.totalPages ?? 1);

        if (this.page > this.totalPages) this.page = this.totalPages;
        if (this.page < 1) this.page = 1;

        this.loading = false;
      },
      error: (err) => {
        this.error = this.extractErrorMessage(err, 'Error al cargar businesses');
        this.loading = false;
      }
    });
  }

  private hasActiveFiltersOrSorting(): boolean {
    const term = this.filtroNombre.trim();
    return (
      term.length > 0 ||
      this.filtroActivo !== undefined ||
      this.sortBy !== 'name' ||
      this.sortDir !== 'asc'
    );
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  toggleSortDir(): void {
    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    this.page = 1;
    this.cargarBusinesses();
  }

  limpiarFiltro(): void {
    this.filtroNombre = '';
    this.filtroActivo = undefined;
    this.sortBy = 'name';
    this.sortDir = 'asc';
    this.page = 1;
    this.cargarBusinesses();
  }

  buscar(): void {
    this.page = 1;
    this.cargarBusinesses();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  cambiarPagina(page: number): void {
    const newPage = page + 1;
    if (newPage < 1 || newPage > this.totalPages) return;
    this.page = newPage;
    if (this.hasActiveFiltersOrSorting()) this.applyClientFilters(false);
    else this.cargarBusinesses();
  }

  private applyClientFilters(resetPage: boolean = true): void {
    const base = [...this.allBusinesses];

    const term = this.filtroNombre.trim().toLowerCase();
    let filtered = base;

    if (term) {
      filtered = filtered.filter(b =>
        (b.name ?? '').toLowerCase().includes(term) ||
        (b.identificationNumber ?? '').toLowerCase().includes(term)
      );
    }

    if (this.filtroActivo !== undefined) {
      filtered = filtered.filter(b => (b.isActive ?? false) === this.filtroActivo);
    }

    const dir = this.sortDir === 'asc' ? 1 : -1;
    filtered.sort((a, b) => {
      if (this.sortBy === 'identification') {
        const av = (a.identificationNumber ?? '').toLowerCase();
        const bv = (b.identificationNumber ?? '').toLowerCase();
        return av.localeCompare(bv) * dir;
      }
      const av = (a.name ?? '').toLowerCase();
      const bv = (b.name ?? '').toLowerCase();
      return av.localeCompare(bv) * dir;
    });

    this.totalFiltered = filtered.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalFiltered / this.pageSize));

    if (resetPage) this.page = 1;
    this.page = Math.min(this.page, this.totalPages);
    if (this.page < 1) this.page = 1;

    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.businesses = filtered.slice(start, end);
  }

  nuevoBusiness(): void {
    this.router.navigate(['/businesses/crear']);
  }

  editarBusiness(b: BusinessResponse): void {
    if (!b?.id) return;
    this.router.navigate(['/businesses/editar', b.id]);
  }

  /**
   * INC 16: solo se pueden eliminar negocios INACTIVOS y exige la contraseña del
   * usuario logueado. Si el negocio está activo, no se permite; hay que desactivarlo
   * primero. Si está inactivo, abre el diálogo de confirmación con contraseña.
   */
  eliminarBusiness(b: BusinessResponse): void {
    if (!b?.id) return;

    if (this.esProveedora(b)) {
      this.toast.warning({ title: 'No permitido', message: PROVIDER_IMMUTABLE_MESSAGE });
      return;
    }

    if (b.isActive ?? false) {
      this.toast.warning({
        title: 'No permitido',
        message: 'Solo se pueden eliminar negocios inactivos. Desactívalo primero.'
      });
      return;
    }

    this.deleteTarget = b;
    this.deletePassword = '';
    this.deleteError = '';
    this.showDeletePassword = false;
    this.showDeleteModal = true;
  }

  toggleDeletePassword(): void {
    this.showDeletePassword = !this.showDeletePassword;
  }

  cancelarEliminar(): void {
    if (this.deleting) return;
    this.showDeleteModal = false;
    this.deleteTarget = null;
    this.deletePassword = '';
    this.deleteError = '';
  }

  confirmarEliminar(): void {
    const b = this.deleteTarget;
    if (!b?.id) return;

    const pwd = this.deletePassword.trim();
    if (!pwd) {
      this.deleteError = 'Ingresa tu contraseña para confirmar.';
      return;
    }

    this.deleting = true;
    this.deleteError = '';

    this.businessApi.deleteWithPassword(b.id, pwd).subscribe({
      next: () => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.deleteTarget = null;
        this.deletePassword = '';
        // Si se cierra el detalle de este negocio, ciérralo también.
        if (this.selectedBusiness?.id === b.id) this.cerrarDetalle();
        if (this.page > 1 && this.businesses.length === 1) this.page--;
        this.cargarBusinesses();
        this.toast.deleted('Empresa');
      },
      error: (err) => {
        this.deleting = false;
        // 401 (contraseña incorrecta): mantener el diálogo abierto y mostrar el error dentro.
        if (err?.status === 401) {
          this.deleteError = err?.error?.message?.trim() || 'Contraseña incorrecta.';
          return;
        }
        // 409 (negocio activo) u otros: cerrar y notificar por toast.
        this.showDeleteModal = false;
        this.deleteTarget = null;
        this.toast.error({ title: 'Error', message: this.extractErrorMessage(err, 'No se pudo eliminar el negocio') });
      }
    });
  }

  verBusiness(b: BusinessResponse): void {
    if (!b?.id) return;

    this.error = '';
    this.isDetailOpen = true;
    this.detailLoading = true;

    const normalizedFromList = this.normalizeBusiness({ ...b });
    const fallbackAdmins = normalizedFromList.admins;

    // mostrar al toque lo del listado (incluye admins)
    this.selectedBusiness = normalizedFromList;

    this.businessApi.getById(b.id).subscribe({
      next: (full) => {
        const normalized = this.normalizeBusiness(full);

        // si el detalle no trae admins, mantenemos los del listado
        if (!normalized.admins || normalized.admins.length === 0) {
          normalized.admins = fallbackAdmins;
        }

        this.selectedBusiness = normalized;
        this.detailLoading = false;
      },
      error: (err) => {
        this.detailLoading = false;
        this.error = this.extractErrorMessage(err, 'No se pudo cargar el detalle del business');
      }
    });
  }

  cerrarDetalle(): void {
    this.isDetailOpen = false;
    this.selectedBusiness = null;
    this.detailLoading = false;
  }

  /**
   * Cambia rápidamente el estado Activo↔Inactivo sin abrir el formulario completo.
   * Pide confirmación solo al desactivar (acción con impacto). Refresca la fila con la
   * respuesta del backend (PATCH /api/businesses/{id}/active devuelve el business completo).
   */
  async toggleActivo(b: BusinessResponse): Promise<void> {
    if (!b?.id || this.togglingId !== null) return;

    const currentlyActive = b.isActive ?? false;
    const nextActive = !currentlyActive;

    // La empresa proveedora es el único acceso garantizado del Super Admin: si se
    // desactivara, nadie podría entrar a reactivarla. El backend también lo rechaza (409).
    if (!nextActive && this.esProveedora(b)) {
      this.toast.warning({ title: 'No permitido', message: PROVIDER_IMMUTABLE_MESSAGE });
      return;
    }

    if (!nextActive) {
      const ok = await this.confirmDialog.confirmDanger({
        title: 'Desactivar business',
        message: `¿Desactivar el business "${b.name}"? Un business inactivo no puede ser accedido ni operado por sus usuarios.`,
        confirmText: 'Desactivar'
      });
      if (!ok) return;
    }

    this.togglingId = b.id;
    this.businessApi.setActive(b.id, nextActive).subscribe({
      next: (updated) => {
        const normalized = this.normalizeBusiness(updated);
        this.patchBusinessInLists(normalized);
        if (this.selectedBusiness?.id === normalized.id) {
          this.selectedBusiness = { ...this.selectedBusiness, ...normalized };
        }
        this.togglingId = null;
        this.toast.success(nextActive ? 'Empresa activada' : 'Empresa desactivada');
      },
      error: (err) => {
        this.togglingId = null;
        this.toast.error({ title: 'Error', message: this.extractErrorMessage(err, 'No se pudo cambiar el estado del business') });
      }
    });
  }

  /** Reemplaza el business en las listas visibles/base tras un cambio de estado. */
  private patchBusinessInLists(updated: BusinessResponse): void {
    const replace = (arr: BusinessResponse[]) => {
      const idx = arr.findIndex(x => x.id === updated.id);
      if (idx !== -1) arr[idx] = { ...arr[idx], ...updated };
    };
    replace(this.businesses);
    replace(this.allBusinesses);
  }

  /** ¿Es la empresa proveedora (dueña del CRM)? Inmutable en estado y borrado. */
  esProveedora(b: BusinessResponse): boolean {
    return b?.isProvider === true;
  }

  getEstadoLabel(b: BusinessResponse): string {
    return (b.isActive ?? false) ? 'Activo' : 'Inactivo';
  }

  getEstadoColor(b: BusinessResponse): 'success' | 'error' {
    return (b.isActive ?? false) ? 'success' : 'error';
  }

  private normalizeBusiness(b: any): BusinessResponse {
    const admins = Array.isArray(b?.admins)
      ? b.admins.map((a: any) => ({
          id: a?.id,
          email: a?.email ?? '',
          fullname: a?.fullname ?? '',
          lastname: a?.lastname ?? null,
          roleId: a?.roleId ?? null,
          roleName: a?.roleName ?? null
        }))
      : [];

    return { ...b, admins };
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const raw =
      err?.error?.message ||
      err?.error?.error ||
      err?.error?.detail ||
      (typeof err?.error === 'string' ? err.error : '') ||
      err?.message ||
      '';

    const msg = String(raw).toLowerCase();
    const status = err?.status;

    if (msg.includes('23505') || msg.includes('duplicate') || msg.includes('llave duplicada')) {
      if (msg.includes('identification') || msg.includes('identification_number') || msg.includes('bussines_identification_number')) {
        return 'La identificación (RUC/NIF) ya está registrada. Usa otra.';
      }
      if (msg.includes('mail') || msg.includes('email')) {
        return 'El correo ya está registrado. Usa otro.';
      }
      return 'Ya existe un registro con esos datos. Revisa e intenta otra vez.';
    }

    if (status === 0) return 'No se pudo conectar al servidor. Revisa tu conexión.';
    if (status === 400) return 'Revisa los datos ingresados. Hay campos inválidos.';
    if (status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    if (status === 403) return 'No tienes permisos para realizar esta acción.';
    if (status === 404) return 'No se encontró el recurso solicitado.';
    if (status >= 500) return 'Ocurrió un error en el servidor. Intenta nuevamente.';

    return raw || fallback;
  }
}
